import {
  Flex,
  FormControl,
  FormLabel,
  Select,
  Textarea,
} from "@chakra-ui/react";
import styled from "@emotion/styled";
import { Input, MultiSelect } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { range } from "lodash";
import { FC, useEffect, useMemo } from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import {
  Crop,
  DropSize,
  Task,
  TillingDepth,
  crops,
  dropSizes,
  tasks,
  tillingDepths,
  translateCrop,
  translateDropSize,
  translateTask,
  translateTillingDepth,
} from "../../hooks/api/karl-farm/constants";
import {
  fieldsCompareFn,
  useFields,
} from "../../hooks/api/karl-farm/useFields";
import { useMachines } from "../../hooks/api/karl-farm/useMachines";
import { useUsers } from "../../hooks/api/karl-farm/useUsers";
import { useFirstDayOfWeek } from "../../hooks/useFirstDayOfWeek";
import { useLocale } from "../../hooks/useLocale";
import { useTranslation } from "../../i18n/useTranslation";
import { getFieldLabel } from "../../utils/getFieldLabel";
import { getFullName } from "../../utils/getFullName";

export type TaskFormData = {
  title: string;
  task: Task;
  crop?: Crop;
  tillingDepth?: TillingDepth;
  dropSize?: DropSize;
  startDate: Date;
  startTime: string;
  duration: string;
  fields: string[];
  users: string[];
  machines: string[];
  notes: string;
};
export const names: {
  [key in keyof Required<TaskFormData>]: key;
} = {
  title: "title",
  task: "task",
  crop: "crop",
  tillingDepth: "tillingDepth",
  dropSize: "dropSize",
  startDate: "startDate",
  startTime: "startTime",
  duration: "duration",
  fields: "fields",
  users: "users",
  machines: "machines",
  notes: "notes",
} as const;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const floorMinutesTo30s = (minutes: number) => {
  return minutes - (minutes % 30);
};

const timeOptions = range(2 * 24).map((halfHourIndex) => {
  const hours = Math.floor(halfHourIndex / 2);
  const minutes = (halfHourIndex % 2) * 30;
  const totalMinutes = hours * 60 + minutes;
  return (
    <option key={totalMinutes} value={totalMinutes}>
      {hours.toString().padStart(2, "0")}:{minutes.toString().padStart(2, "0")}
    </option>
  );
});

type TaskFormProps = {
  start?: Date;
  formMethods: UseFormReturn<TaskFormData>;
  defaultValues?: Partial<TaskFormData>;
};
export const TaskForm: FC<TaskFormProps> = ({
  start,
  formMethods,
  defaultValues,
}) => {
  const { t } = useTranslation();
  const { locale } = useLocale();

  const { register, watch, setValue } = formMethods;

  const { data: fieldsData } = useFields();
  const sortedFields = useMemo(
    () => fieldsData?.data?.fields?.sort(fieldsCompareFn),
    [fieldsData]
  );
  const { data: usersData } = useUsers();
  const { data: machinesData } = useMachines();

  const defaultStartDateTime = useMemo(() => {
    if (start != null) {
      return start;
    } else {
      const now = new Date();
      const in30Minutes = new Date(now.getTime());
      in30Minutes.setMinutes(in30Minutes.getMinutes() + 30);
      return in30Minutes;
    }
  }, [start]);

  const safeDefaultValues: Required<TaskFormData> = useMemo(() => {
    return {
      title: "",
      task: "harvesting",
      crop: "barley",
      dropSize: "fine",
      tillingDepth: "shallow",
      startDate: defaultStartDateTime,
      startTime: floorMinutesTo30s(
        defaultStartDateTime.getHours() * 60 + defaultStartDateTime.getMinutes()
      ).toString(),
      duration: "60",
      fields: [],
      users: [],
      machines: [],
      notes: "",
      ...defaultValues,
    };
  }, [defaultValues, defaultStartDateTime]);

  useEffect(() => {
    setValue(names.startDate, safeDefaultValues.startDate);
    setValue(names.startTime, safeDefaultValues.startTime);
  }, [setValue, safeDefaultValues]);

  const task = watch(names.task) ?? safeDefaultValues.task;

  const firstDayOfWeek = useFirstDayOfWeek();

  return (
    <Form>
      <FormControl key={names.title}>
        <FormLabel>{t("component.TaskForm.formLabels.title")}:</FormLabel>
        <Input
          {...register(names.title)}
          defaultValue={safeDefaultValues.title}
        />
      </FormControl>

      <FormControl>
        <FormLabel>{t("component.TaskForm.formLabels.task")}:</FormLabel>
        <Select {...register(names.task)} defaultValue={safeDefaultValues.task}>
          {tasks.map((task) => (
            <option key={task} value={task}>
              {translateTask(t, task)}
            </option>
          ))}
        </Select>
      </FormControl>

      {["harvesting", "seeding"].includes(task) && (
        <FormControl key={names.crop}>
          <FormLabel>{t("component.TaskForm.formLabels.crop")}:</FormLabel>
          <Select
            {...register(names.crop)}
            defaultValue={safeDefaultValues.crop}
          >
            {crops.map((crop) => (
              <option key={crop} value={crop}>
                {translateCrop(t, crop)}
              </option>
            ))}
          </Select>
        </FormControl>
      )}

      {task === "tilling" && (
        <FormControl key={names.tillingDepth}>
          <FormLabel>
            {t("component.TaskForm.formLabels.tillingDepth")}:
          </FormLabel>
          <Select
            {...register(names.tillingDepth)}
            defaultValue={safeDefaultValues.tillingDepth}
          >
            {tillingDepths.map((tillingDepth) => (
              <option key={tillingDepth} value={tillingDepth}>
                {translateTillingDepth(t, tillingDepth)}
              </option>
            ))}
          </Select>
        </FormControl>
      )}

      {task === "spraying" && (
        <FormControl key={names.dropSize}>
          <FormLabel>{t("component.TaskForm.formLabels.dropSize")}:</FormLabel>
          <Select
            {...register(names.dropSize)}
            defaultValue={safeDefaultValues.dropSize}
          >
            {dropSizes.map((dropSize) => (
              <option key={dropSize} value={dropSize}>
                {translateDropSize(t, dropSize)}
              </option>
            ))}
          </Select>
        </FormControl>
      )}

      <Flex direction="column" gap={"3px"}>
        <FormLabel>{t("component.TaskForm.formLabels.start")}:</FormLabel>
        <Controller
          name={names.startDate}
          control={formMethods.control}
          defaultValue={safeDefaultValues.startDate}
          render={({ field }) => (
            <DatePicker
              size="md"
              firstDayOfWeek={firstDayOfWeek}
              locale={locale}
              {...field}
            />
          )}
        />
        <Select
          {...register(names.startTime)}
          defaultValue={safeDefaultValues.startTime}
        >
          {timeOptions}
        </Select>
      </Flex>

      <Flex direction="column" gap={"3px"}>
        <FormLabel>{t("component.TaskForm.formLabels.duration")}:</FormLabel>
        <Select
          {...register(names.duration)}
          defaultValue={safeDefaultValues.duration}
        >
          {timeOptions}
        </Select>
      </Flex>

      {sortedFields != null && (
        <FormControl>
          <FormLabel>{t("component.TaskForm.formLabels.fields")}:</FormLabel>
          <Controller
            name={names.fields}
            control={formMethods.control}
            defaultValue={safeDefaultValues.fields}
            rules={{
              validate: (value) => (task === "other" ? true : value.length > 0),
            }}
            render={({ field }) => (
              <MultiSelect
                ref={field.ref}
                searchable
                value={field.value}
                onChange={(value) => field.onChange(value)}
                data={
                  // It's okay to assume that the data is there, because we
                  // check for it above.
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  sortedFields.map((field) => {
                    return {
                      value: field.id,
                      label: getFieldLabel(field),
                    };
                  })
                }
              />
            )}
          />
        </FormControl>
      )}

      {usersData?.data?.users != null && (
        <FormControl>
          <FormLabel>{t("component.TaskForm.formLabels.users")}:</FormLabel>
          <Controller
            name={names.users}
            control={formMethods.control}
            defaultValue={safeDefaultValues.users}
            rules={{ validate: (value) => value.length > 0 }}
            render={({ field }) => (
              <MultiSelect
                ref={field.ref}
                searchable
                value={field.value}
                onChange={(value) => field.onChange(value)}
                data={
                  // It's okay to assume that the data is there, because we
                  // check for it above.
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  usersData.data.users!.map((user, i) => ({
                    value: user.Attributes.sub || i.toString(),
                    label:
                      getFullName(
                        user.Attributes.name,
                        user.Attributes.family_name
                      ) || "n/a",
                  }))
                }
              />
            )}
          />
        </FormControl>
      )}

      {machinesData?.data.machines != null && (
        <FormControl>
          <FormLabel>
            {t("component.TaskForm.formLabels.machinesAndEquipment")}:
          </FormLabel>
          <Controller
            name={names.machines}
            control={formMethods.control}
            defaultValue={safeDefaultValues.machines}
            rules={{
              validate: (value) => (task === "other" ? true : value.length > 0),
            }}
            render={({ field }) => (
              <MultiSelect
                ref={field.ref}
                searchable
                value={field.value}
                onChange={(value) => field.onChange(value)}
                data={machinesData.data.machines.map((machine) => ({
                  value: machine.machine_id,
                  label: getFullName(machine.brand, machine.model) || "n/a",
                }))}
              />
            )}
          />
        </FormControl>
      )}

      <FormControl>
        <FormLabel>{t("component.TaskForm.formLabels.notes")}:</FormLabel>
        <Textarea
          {...register(names.notes)}
          defaultValue={safeDefaultValues.notes}
        />
      </FormControl>
    </Form>
  );
};
